import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { BadgeEsito } from './Badge';
import { oliApi, erroreOli, adessoLocale, esitoOlio } from './api';

const CAMPO = 'w-full h-11 px-3 rounded-lg border border-gray-300 mt-1';
const VISIVO = { colore: 'chiaro', fumo: false, schiuma: false, odore_acre: false };
const AZIONI = [['nessuna', 'Nessuna'], ['filtrato', 'Filtrato'], ['cambiato', 'Olio cambiato']];
const SEGNI = [['fumo', 'Fuma già a 180 °C'], ['schiuma', 'Schiuma che non sparisce'], ['odore_acre', 'Odore acre / di rancido']];

const Scelta = ({ attivo, onClick, testid, children }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex-1 h-10 rounded-lg text-sm font-medium border ${attivo ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-gray-700 border-gray-300'}`}>
    {children}
  </button>
);

// Controllo dell'olio di frittura: tester composti polari (limite 25%) o controllo a vista, e cosa si è fatto
export const ControlloOlioDialog = ({ friggitrice, onClose, onSaved }) => {
  const [metodo, setMetodo] = useState('tester');
  const [polari, setPolari] = useState('');
  const [visivo, setVisivo] = useState(VISIVO);
  const [temperatura, setTemperatura] = useState('');
  const [azione, setAzione] = useState('nessuna');
  const [litri, setLitri] = useState('');
  const [dataOra, setDataOra] = useState(adessoLocale());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!friggitrice) return;
    setMetodo('tester'); setPolari(''); setVisivo(VISIVO); setTemperatura(''); setAzione('nessuna');
    setLitri(friggitrice.capacita_litri ? String(friggitrice.capacita_litri) : ''); setDataOra(adessoLocale()); setNote('');
  }, [friggitrice]);

  const esito = esitoOlio({ metodo, polari, visivo, temperatura });

  const salva = async () => {
    const body = { friggitrice_id: friggitrice.id, data_ora: dataOra, azione, note: note || null,
      temperatura: temperatura === '' ? null : Number(temperatura),
      polari_percentuale: metodo === 'tester' ? Number(polari) : null, visivo: metodo === 'visivo' ? visivo : null,
      litri_cambiati: azione === 'cambiato' && litri !== '' ? Number(litri) : null };
    setBusy(true);
    try {
      const res = await oliApi.post('/friggitrici/controlli', body);
      if (res.avviso) toast.warning(res.avviso); else toast.success('Controllo registrato');
      onSaved();
    } catch (e) {
      toast.error(erroreOli(e, 'Non sono riuscito a salvare'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!friggitrice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="oli-controllo-dialog">
        <DialogHeader>
          <DialogTitle>Controllo olio · {friggitrice?.nome}</DialogTitle>
          <DialogDescription>Limite: 25% di composti polari. Non superare 180 °C e non rabboccare olio nuovo su quello vecchio.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Scelta attivo={metodo === 'tester'} onClick={() => setMetodo('tester')} testid="oli-controllo-metodo-tester">Con il tester</Scelta>
          <Scelta attivo={metodo === 'visivo'} onClick={() => setMetodo('visivo')} testid="oli-controllo-metodo-visivo">A vista</Scelta>
        </div>
        {metodo === 'tester' ? (
          <label className="text-sm font-medium text-gray-700">Composti polari (%)
            <input type="number" step="0.5" min="0" max="60" value={polari} onChange={(e) => setPolari(e.target.value)}
              className={CAMPO} placeholder="es. 14" data-testid="oli-controllo-polari" />
          </label>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              {['chiaro', 'ambrato', 'scuro'].map((c) => (
                <Scelta key={c} attivo={visivo.colore === c} onClick={() => setVisivo({ ...visivo, colore: c })} testid={`oli-controllo-colore-${c}`}>{c}</Scelta>
              ))}
            </div>
            {SEGNI.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={visivo[k]} onChange={(e) => setVisivo({ ...visivo, [k]: e.target.checked })} data-testid={`oli-controllo-${k}`} />
                {label}
              </label>
            ))}
          </div>
        )}
        <label className="text-sm font-medium text-gray-700">Temperatura di frittura °C (facoltativa)
          <input type="number" value={temperatura} onChange={(e) => setTemperatura(e.target.value)} className={CAMPO} data-testid="oli-controllo-temperatura" />
        </label>
        {esito && (
          <div className="flex items-center gap-2 text-sm" data-testid="oli-controllo-esito">
            Esito: <BadgeEsito esito={esito} />
            {esito === 'non_conforme' && azione !== 'cambiato' && <span className="text-red-700 text-xs">va cambiato</span>}
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">Cosa hai fatto</p>
          <div className="flex gap-2">
            {AZIONI.map(([v, label]) => <Scelta key={v} attivo={azione === v} onClick={() => setAzione(v)} testid={`oli-controllo-azione-${v}`}>{label}</Scelta>)}
          </div>
        </div>
        {azione === 'cambiato' && (
          <label className="text-sm font-medium text-gray-700">Litri sostituiti
            <input type="number" step="0.5" value={litri} onChange={(e) => setLitri(e.target.value)} className={CAMPO} data-testid="oli-controllo-litri" />
          </label>
        )}
        <label className="text-sm font-medium text-gray-700">Data e ora
          <input type="datetime-local" value={dataOra} max={adessoLocale()} onChange={(e) => setDataOra(e.target.value)} className={CAMPO} data-testid="oli-controllo-data" />
        </label>
        <label className="text-sm font-medium text-gray-700">Note
          <input value={note} onChange={(e) => setNote(e.target.value)} className={CAMPO} data-testid="oli-controllo-note" />
        </label>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} data-testid="oli-controllo-chiudi">Chiudi</Button>
          <Button onClick={salva} disabled={busy || !esito} className="bg-orange-700 hover:bg-orange-800 text-white" data-testid="oli-controllo-salva">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Registra'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
